import React, { useCallback, useEffect } from 'react';
import {
  Paper,
  Typography,
  Box,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Alert,
  CircularProgress,
  FormControlLabel,
  Switch,
} from '@mui/material';
import { Refresh, FiberManualRecord } from '@mui/icons-material';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import type { RootState } from '../../store';
import { fetchActiveDevices, clearDeviceError } from '../../store/slices/adminDeviceSlice';

const AUTO_REFRESH_INTERVAL_MS = 10000;

const ActiveDevices: React.FC = () => {
  const dispatch = useAppDispatch();
  const { adminApiKey } = useAppSelector((state: RootState) => state.adminAuth);
  const { activeDevices, activeLoading, error } = useAppSelector(
    (state: RootState) => state.adminDevices
  );
  const [autoRefresh, setAutoRefresh] = React.useState(true);

  const refresh = useCallback(() => {
    if (adminApiKey) {
      dispatch(fetchActiveDevices(adminApiKey));
    }
  }, [adminApiKey, dispatch]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  useEffect(() => {
    if (!autoRefresh) return;
    const interval = setInterval(refresh, AUTO_REFRESH_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [autoRefresh, refresh]);

  return (
    <Paper elevation={3} sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="h5">Active Subscribed Devices</Typography>
          <Chip
            size="small"
            color="success"
            label={`${activeDevices.length} online`}
            icon={<FiberManualRecord />}
          />
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <FormControlLabel
            control={
              <Switch
                checked={autoRefresh}
                onChange={(e) => setAutoRefresh(e.target.checked)}
                size="small"
              />
            }
            label="Auto-refresh"
          />
          <Button startIcon={<Refresh />} onClick={refresh} disabled={activeLoading}>
            Refresh
          </Button>
        </Box>
      </Box>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Devices currently holding a live subscription (Server-Sent-Events connection).
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => dispatch(clearDeviceError())}>
          {error}
        </Alert>
      )}

      {activeLoading && activeDevices.length === 0 ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Serial Number</TableCell>
                <TableCell>Client Group</TableCell>
                <TableCell>City / Location</TableCell>
                <TableCell>Model</TableCell>
                <TableCell>Last Sent Route</TableCell>
                <TableCell>Last Sent Route Date</TableCell>
                <TableCell>Registered</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {activeDevices.map((device) => (
                <TableRow key={device.serialNo} hover>
                  <TableCell>{device.serialNo}</TableCell>
                  <TableCell>
                    {device.clientName ? (
                      <Chip size="small" label={`${device.clientName} (${device.clientId})`} />
                    ) : (
                      <Chip size="small" color="warning" label="Unassigned" variant="outlined" />
                    )}
                  </TableCell>
                  <TableCell>
                    {[device.city, device.location].filter(Boolean).join(' / ') || '—'}
                  </TableCell>
                  <TableCell>{device.model || '—'}</TableCell>
                  <TableCell>{device.lastSentRouteShipmentNumber || '—'}</TableCell>
                  <TableCell>{device.lastSentRouteDate || '—'}</TableCell>
                  <TableCell>
                    {device.registered ? (
                      <Chip size="small" color="success" label="Yes" variant="outlined" />
                    ) : (
                      <Chip size="small" color="default" label="No" variant="outlined" />
                    )}
                  </TableCell>
                </TableRow>
              ))}
              {activeDevices.length === 0 && (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                    <Typography color="text.secondary">
                      No devices are currently subscribed.
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Paper>
  );
};

export default ActiveDevices;
